import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  Table, Input, Button, Switch, Tag, Card, App as AntdApp, Modal, Tooltip, Row, Col,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { Plus, Pencil, Trash2, MessageSquare, Search, Lightbulb, BarChart3, ArrowUp, ArrowDown } from 'lucide-react';
import { PageHeader } from '@/components/common/PageHeader';
import {
  adminFeedbackService, type FeedbackTopic,
} from '@/services/feedbackService';

/**
 * V1.0.37: Trang quản lý Chủ đề lắng nghe (image 2 của design).
 */
export function FeedbackTopicsPage() {
  const { message, modal } = AntdApp.useApp();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [search, setSearch] = useState('');

  const { data: topics = [], isLoading } = useQuery({
    queryKey: ['admin-feedback-topics'],
    queryFn: () => adminFeedbackService.listTopics(true),
  });

  const toggleMut = useMutation({
    mutationFn: (id: string) => adminFeedbackService.toggleTopic(id),
    onSuccess: () => {
      message.success('Đã cập nhật trạng thái');
      qc.invalidateQueries({ queryKey: ['admin-feedback-topics'] });
    },
    onError: (e: any) => message.error(e?.response?.data?.message ?? 'Cập nhật thất bại'),
  });

  const deleteMut = useMutation({
    mutationFn: (id: string) => adminFeedbackService.deleteTopic(id),
    onSuccess: () => {
      message.success('Đã xóa chủ đề');
      qc.invalidateQueries({ queryKey: ['admin-feedback-topics'] });
    },
    onError: (e: any) => message.error(e?.response?.data?.message ?? 'Xóa thất bại'),
  });

  // V1.0.43: Đổi STT chủ đề
  const moveMut = useMutation({
    mutationFn: ({ id, direction }: { id: string; direction: 'up' | 'down' }) =>
      adminFeedbackService.moveTopic(id, direction),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-feedback-topics'] });
    },
    onError: (e: any) => message.error(e?.response?.data?.message ?? 'Đổi thứ tự thất bại'),
  });

  // V1.0.43: Sort theo sortOrder để hiển thị đúng thứ tự (FE phòng trường hợp BE đổi sort)
  const sortedTopics = [...topics].sort((a, b) => a.sortOrder - b.sortOrder);

  // Filter
  const filtered = sortedTopics.filter(t =>
    !search || t.name.toLowerCase().includes(search.toLowerCase())
  );

  // Stats
  const totalTopics = topics.length;
  const activeCount = topics.filter(t => t.isActive).length;
  const totalFeedback = topics.reduce((acc, t) => acc + t.feedbackCount, 0);
  const topActive = [...topics].sort((a, b) => b.feedbackCount - a.feedbackCount)[0];

  const columns: ColumnsType<FeedbackTopic> = [
    {
      title: 'STT',
      key: 'sort',
      width: 100,
      align: 'center',
      render: (_, r, idx) => {
        const isFirst = idx === 0;
        const isLast = idx === filtered.length - 1;
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: 4, justifyContent: 'center' }}>
            <span style={{
              fontWeight: 600, color: '#1e6feb',
              minWidth: 20, textAlign: 'center',
            }}>
              {idx + 1}
            </span>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Tooltip title="Lên 1 vị trí">
                <Button
                  type="text" size="small"
                  style={{ height: 18, padding: 0, minWidth: 20 }}
                  icon={<ArrowUp size={11} />}
                  disabled={isFirst || (moveMut.isPending && (moveMut.variables as any)?.id === r.id)}
                  onClick={() => moveMut.mutate({ id: r.id, direction: 'up' })}
                />
              </Tooltip>
              <Tooltip title="Xuống 1 vị trí">
                <Button
                  type="text" size="small"
                  style={{ height: 18, padding: 0, minWidth: 20 }}
                  icon={<ArrowDown size={11} />}
                  disabled={isLast || (moveMut.isPending && (moveMut.variables as any)?.id === r.id)}
                  onClick={() => moveMut.mutate({ id: r.id, direction: 'down' })}
                />
              </Tooltip>
            </div>
          </div>
        );
      },
    },
    {
      title: 'CHỦ ĐỀ LẮNG NGHE',
      key: 'topic',
      render: (_, r) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{
            width: 40, height: 40, borderRadius: 8,
            background: '#f0f4ff', display: 'flex',
            alignItems: 'center', justifyContent: 'center',
            fontSize: 20,
          }}>
            {r.emoji || '💬'}
          </div>
          <div>
            <div style={{ fontWeight: 600, color: '#1e293b' }}>{r.name}</div>
            <div style={{ fontSize: 11, color: '#7d8a98' }}>Mã: {r.code}</div>
          </div>
        </div>
      ),
    },
    {
      title: 'NGÀY TẠO',
      dataIndex: 'createdAt',
      width: 130,
      render: (v: string) => new Date(v).toLocaleDateString('vi-VN'),
    },
    {
      title: 'Ý KIẾN',
      dataIndex: 'feedbackCount',
      width: 100,
      align: 'center',
      render: (v: number) => (
        <span style={{ fontSize: 14, fontWeight: 600, color: '#1e6feb' }}>{v}</span>
      ),
    },
    {
      title: 'TRẠNG THÁI',
      key: 'status',
      width: 180,
      render: (_, r) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Switch
            checked={r.isActive}
            loading={toggleMut.isPending && toggleMut.variables === r.id}
            onChange={() => toggleMut.mutate(r.id)}
          />
          <Tag color={r.isActive ? 'green' : 'default'}>
            {r.isActive ? 'ĐANG MỞ' : 'ĐÃ ĐÓNG'}
          </Tag>
        </div>
      ),
    },
    {
      title: 'THAO TÁC',
      key: 'actions',
      width: 110,
      align: 'center',
      render: (_, r) => (
        <div style={{ display: 'flex', gap: 6, justifyContent: 'center' }}>
          <Tooltip title="Sửa">
            <Button
              type="text" size="small"
              icon={<Pencil size={14} />}
              onClick={() => navigate(`/admin/feedback/topics/${r.id}/edit`)}
            />
          </Tooltip>
          <Tooltip title="Xóa">
            <Button
              type="text" danger size="small"
              icon={<Trash2 size={14} />}
              onClick={() => {
                modal.confirm({
                  title: 'Xóa chủ đề?',
                  content: r.feedbackCount > 0
                    ? `Chủ đề "${r.name}" có ${r.feedbackCount} phản hồi. Xóa sẽ làm các phản hồi mất chủ đề. Tiếp tục?`
                    : `Xóa chủ đề "${r.name}"?`,
                  okText: 'Xóa',
                  okButtonProps: { danger: true },
                  cancelText: 'Hủy',
                  onOk: () => deleteMut.mutate(r.id),
                });
              }}
            />
          </Tooltip>
        </div>
      ),
    },
  ];

  return (
    <>
      <PageHeader
        breadcrumb="QUẢN TRỊ"
        title="Quản lý chủ đề lắng nghe"
        subtitle="Hộp thư lắng nghe: Nơi tiếp nhận và cải thiện chất lượng phục vụ từ tâm."
        extra={
          <Button
            type="primary"
            size="large"
            icon={<Plus size={14} />}
            onClick={() => navigate('/admin/feedback/topics/new')}
          >
            Mở thêm chủ đề lắng nghe
          </Button>
        }
      />

      {/* Stats cards — responsive via Row/Col */}
      <Row gutter={[16, 16]} style={{ marginBottom: 20 }}>
        <Col xs={24} sm={12} md={8}>
          <Card bodyStyle={{ padding: 20 }}>
            <div style={{ fontSize: 11, color: '#7d8a98', textTransform: 'uppercase', fontWeight: 600 }}>
              Tổng chủ đề
            </div>
            <div style={{ fontSize: 32, fontWeight: 700, color: '#1e6feb', marginTop: 6 }}>
              {totalTopics}
            </div>
          </Card>
        </Col>
        <Col xs={24} sm={12} md={8}>
          <Card bodyStyle={{ padding: 20 }}>
            <div style={{ fontSize: 11, color: '#7d8a98', textTransform: 'uppercase', fontWeight: 600 }}>
              Đang lắng nghe
            </div>
            <div style={{ fontSize: 32, fontWeight: 700, color: '#52c41a', marginTop: 6 }}>
              {activeCount}
            </div>
            <div style={{ fontSize: 11, color: '#7d8a98', marginTop: 4 }}>
              {totalTopics > 0 ? `${Math.round(activeCount * 100 / totalTopics)}% tổng số chủ đề` : '—'}
            </div>
          </Card>
        </Col>
        <Col xs={24} sm={24} md={8}>
          <Card bodyStyle={{ padding: 20 }}>
            <div style={{ fontSize: 11, color: '#7d8a98', textTransform: 'uppercase', fontWeight: 600 }}>
              Ý kiến đã nhận
            </div>
            <div style={{ fontSize: 32, fontWeight: 700, color: '#1e293b', marginTop: 6 }}>
              {totalFeedback.toLocaleString('vi-VN')}
            </div>
          </Card>
        </Col>
      </Row>

      <Card style={{ marginBottom: 16 }}>
        <Input
          placeholder="Tìm kiếm tên chủ đề..."
          prefix={<Search size={14} style={{ color: '#7d8a98' }} />}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          allowClear
          style={{ maxWidth: 400 }}
        />
      </Card>

      <Table
        dataSource={filtered}
        columns={columns}
        rowKey="id"
        loading={isLoading}
        pagination={{ pageSize: 20, showSizeChanger: true }}
        scroll={{ x: 800 }}
      />

      {/* Insight cards — responsive */}
      <Row gutter={[16, 16]} style={{ marginTop: 20 }}>
        <Col xs={24} md={12}>
          <Card style={{ borderLeft: '3px solid #1e6feb' }}>
            <div style={{ display: 'flex', gap: 12 }}>
              <Lightbulb size={20} style={{ color: '#1e6feb', flexShrink: 0 }} />
              <div>
                <div style={{ fontWeight: 600, marginBottom: 6 }}>Hộp thư thấu hiểu</div>
                <div style={{ fontSize: 12, color: '#5b6875' }}>
                  Sắp xếp theo số lượng ý kiến để thấu hiểu nhanh nhất những mong đợi từ bệnh nhân và người nhà.
                </div>
              </div>
            </div>
          </Card>
        </Col>
        <Col xs={24} md={12}>
          <Card style={{ borderLeft: '3px solid #52c41a' }}>
            <div style={{ display: 'flex', gap: 12 }}>
              <BarChart3 size={20} style={{ color: '#52c41a', flexShrink: 0 }} />
              <div>
                <div style={{ fontWeight: 600, marginBottom: 6 }}>Chỉ số lắng nghe</div>
                <div style={{ fontSize: 12, color: '#5b6875' }}>
                  {topActive
                    ? `Chủ đề "${topActive.name}" đang nhận được nhiều ý kiến nhất (${topActive.feedbackCount} phản hồi).`
                    : 'Chưa có đủ dữ liệu để hiển thị insight.'}
                </div>
              </div>
            </div>
          </Card>
        </Col>
      </Row>
    </>
  );
}

export default FeedbackTopicsPage;
